export type ExecutionStepStatus = "pending" | "active" | "done" | "failed";

export interface ExecutionStep {
  label: string;
  status: ExecutionStepStatus;
}

const EXECUTION_STEP_LABELS: Record<string, string[]> = {
  "tenant.restart": ["Restarting API…", "Running Runtime Doctor…", "Verifying health…"],
  "restart-tenant-api": ["Restarting API…", "Running Runtime Doctor…", "Verifying health…"],
  "tenant.redeploy": ["Redeploying tenant stack…", "Running Runtime Doctor…", "Verifying health…"],
  "redeploy-tenant": ["Redeploying tenant stack…", "Running Runtime Doctor…", "Verifying health…"],
  "backup.run": ["Running backup…", "Verifying backup…"],
  "run-backup": ["Running backup…", "Verifying backup…"],
  "email.reconnect": ["Restarting API…", "Retrying email checks…", "Verifying health…"],
  "retry-email": ["Restarting API…", "Retrying email checks…", "Verifying health…"],
  "verification.rerun": ["Running Runtime Doctor…", "Verifying platform…"],
  "run-doctor": ["Running Runtime Doctor…", "Verifying platform…"],
  "knowledgebase.reindex": ["Reindexing Knowledge Base…", "Verifying index…"],
  "dns.reverify": ["Re-running DNS verification…"],
  "tls.reverify": ["Re-running TLS verification…"],
  "integration.test": ["Testing integration…", "Recording verification…"],
  "shopify.sync": ["Syncing Shopify…", "Recording verification…"],
};

export function initialExecutionSteps(actionId: string): ExecutionStep[] {
  const labels = EXECUTION_STEP_LABELS[actionId] ?? ["Executing mitigation…", "Verifying platform…"];
  return labels.map((label, i) => ({ label, status: i === 0 ? "active" : "pending" }));
}

export function advanceExecutionSteps(steps: ExecutionStep[]): ExecutionStep[] {
  const activeIdx = steps.findIndex((s) => s.status === "active");
  if (activeIdx === -1) return steps;
  return steps.map((step, i) => {
    if (i < activeIdx) return { ...step, status: "done" as const };
    if (i === activeIdx) return { ...step, status: "done" as const };
    if (i === activeIdx + 1) return { ...step, status: "active" as const };
    return step;
  });
}

export function completeExecutionSteps(steps: ExecutionStep[], success: boolean): ExecutionStep[] {
  if (!success) {
    const activeIdx = steps.findIndex((s) => s.status === "active");
    return steps.map((step, i) => {
      if (i < activeIdx) return { ...step, status: "done" as const };
      if (i === activeIdx) return { ...step, status: "failed" as const };
      return { ...step, status: "pending" as const };
    });
  }
  return steps.map((step) => ({ ...step, status: "done" as const }));
}

export function finalExecutionLabel(success: boolean, resolved: boolean): string {
  if (!success) return "Mitigation failed — review evidence and retry.";
  if (resolved) return "Verified outcome — Runtime Doctor reported healthy.";
  return "Action finished — verification required before treating as resolved.";
}

export function verificationVerdict(result: { success: boolean; verification?: Record<string, unknown> }): boolean {
  const v = result.verification?.verdict;
  return v === "healthy" || result.success;
}

/** Rotate through pending steps while awaiting the API (simulated live progress). */
export function tickExecutionSteps(steps: ExecutionStep[]): ExecutionStep[] {
  const activeIdx = steps.findIndex((s) => s.status === "active");
  if (activeIdx === -1 || activeIdx >= steps.length - 1) return steps;
  return advanceExecutionSteps(steps);
}

export const EVIDENCE_GROUP_LABELS: Record<string, string> = {
  doctor_check: "Automated checks",
  container_state: "Container state",
  health_endpoint: "Health endpoint",
  deployment: "Deployment",
  manifest: "Manifest",
  logs: "Logs",
  backup: "Backup",
  other: "Other",
};

/** Makes Policy Engine evidence kinds understandable in the Evidence panel. */
export const EVIDENCE_KIND_LABELS: Record<string, string> = {
  policy_input: "Policy input",
  verification: "Supporting verification",
  doctor_check: "Doctor check",
  container_state: "Container state",
  health_endpoint: "Health endpoint",
  deployment: "Deployment",
  logs: "Logs",
  backup: "Backup",
};
